import React, { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'
import { ChevronRight, MoreHorizontal } from 'lucide-react'
import UserIcon from 'components/users/UserIcon'
import answerModel from 'components/answers/answerModel'
import { getCachedAnswer } from 'state/cache'
import { crumbWords, layoutAncestorCrumbs } from 'lib/ancestorCrumbs'
import type { Answer } from 'types/answers'
import { getParentPayload, normalizeApiUrl, type ParentPayload } from './crumbPayload'
import styles from './ancestor-crumbs.module.scss'

// fixed_header: a thread's ancestor posts as breadcrumbs. Each crumb is its
// author's avatar and the post's first words (or, for a media-only post, a
// tiny thumbnail and the media's kind). Long chains keep the root and the
// nearest parent and fold the levels between into a "⋯ N" badge whose
// dropdown names every one.

const KIND_LABEL: Record<ParentPayload['kind'], string> = {
    image: 'Photo',
    video: 'Video',
    audio: 'Audio',
    ordinal: 'Ordinal',
    url: 'Link',
    file: 'File',
    other: 'Media',
}

// The read payload's ancestors are slim (author, a text preview); a post
// already in the cache has its media too.
function crumbParts(answer: Answer) {
    const full: any = (answer?.id && getCachedAnswer(String(answer.id))) || answer
    const payload = getParentPayload(full)
    const words = crumbWords(answerModel.getText(full) || (answer as any)?.content_preview)
    const hidden = !answer?.user
    return {
        payload,
        label:
            words ||
            (payload ? KIND_LABEL[payload.kind] : hidden ? 'Private' : answer.user?.name || 'Post'),
        isKind: !words,
    }
}

function Thumb({ payload }: { payload: ParentPayload | null }) {
    if (!payload) return null
    const previewUrl = 'previewUrl' in payload ? payload.previewUrl : null
    if (previewUrl) {
        return <img className={styles.thumb} src={normalizeApiUrl(previewUrl) || ''} alt="" />
    }
    if ('icon' in payload) {
        const Icon = payload.icon
        return (
            <span className={cn(styles.thumb, styles.thumbIcon)}>
                <Icon size={10} strokeWidth={2.4} />
            </span>
        )
    }
    return null
}

function Crumb({ answer, onClick }: { answer: Answer; onClick: () => void }) {
    const { payload, label, isKind } = crumbParts(answer)
    return (
        <button
            type="button"
            className={styles.crumb}
            onClick={ev => {
                ev.stopPropagation()
                onClick()
            }}
            title={answerModel.getText(answer) || label}
        >
            {answer?.user && (
                <UserIcon user={answer.user} contextStyles={styles.avatar} size="small" />
            )}
            <Thumb payload={payload} />
            <span data-crumb-words className={cn(styles.words, isKind && styles.kind)}>
                {label}
            </span>
        </button>
    )
}

export default function AncestorCrumbs({
    ancestors,
    onOpen,
    leadingSeparator = false,
    compact = false,
    className,
}: {
    ancestors: Answer[]
    onOpen: (index: number) => void
    // A chevron before the first crumb (after the channel, in the header).
    leadingSeparator?: boolean
    // Phones: the nearest parent always stays a crumb; the root joins it only
    // when everything fits untruncated, and the rest fold.
    compact?: boolean
    className?: string
}) {
    const badgeRef = useRef<HTMLButtonElement>(null)
    const rootRef = useRef<HTMLSpanElement>(null)
    const [menu, setMenu] = useState<{ top: number; left: number } | null>(null)
    // Compact: try the root inline first; fold it if anything gets cut off.
    const [rootFits, setRootFits] = useState<boolean | null>(null)
    const { folded, inline } = layoutAncestorCrumbs(ancestors.length, {
        compact: compact && rootFits === false,
    })

    useLayoutEffect(() => {
        if (!compact || rootFits !== null) return
        const root = rootRef.current
        if (!root) return
        // Cut short for lack of room, that is: words already at their own
        // max-width cap would be cut the same either way.
        const truncated = [...root.querySelectorAll<HTMLElement>('[data-crumb-words]')].some(el => {
            if (el.scrollWidth <= el.clientWidth + 1) return false
            const cap = parseFloat(getComputedStyle(el).maxWidth)
            return !(Number.isFinite(cap) && el.clientWidth >= cap - 1)
        })
        setRootFits(!truncated && root.scrollWidth <= root.clientWidth + 1)
    }, [compact, rootFits])

    // Re-try when the room or the chain changes.
    const measuredLengthRef = useRef(ancestors.length)
    useLayoutEffect(() => {
        if (!compact) return
        if (measuredLengthRef.current !== ancestors.length) {
            measuredLengthRef.current = ancestors.length
            setRootFits(null)
        }
        const host = rootRef.current?.parentElement
        if (!host || typeof ResizeObserver === 'undefined') return
        let width = host.clientWidth
        const observer = new ResizeObserver(() => {
            if (Math.abs(host.clientWidth - width) < 1) return
            width = host.clientWidth
            setRootFits(null)
        })
        observer.observe(host)
        return () => observer.disconnect()
    }, [compact, ancestors.length])

    if (!ancestors.length) return null

    const openMenu = (ev: React.MouseEvent) => {
        ev.stopPropagation()
        const rect = badgeRef.current?.getBoundingClientRect()
        if (!rect) return
        const width = 300
        setMenu({
            top: rect.bottom + 6,
            left: Math.max(12, Math.min(rect.left, window.innerWidth - 12 - width)),
        })
    }

    const separator = <ChevronRight className={styles.separator} size={12} strokeWidth={2.6} />

    return (
        <span ref={rootRef} className={cn(styles.crumbs, className)}>
            {/* In chain order: each inline crumb, and the badge where the
                folded levels sit (between the root and the parent). */}
            {ancestors.map((answer, i) => {
                const lead = (i > 0 || leadingSeparator) && separator
                if (inline.includes(i)) {
                    return (
                        <React.Fragment key={String(answer?.id ?? i)}>
                            {lead}
                            <Crumb answer={answer} onClick={() => onOpen(i)} />
                        </React.Fragment>
                    )
                }
                if (i !== folded[0]) return null
                return (
                    <React.Fragment key="folded">
                        {lead}
                        <button
                            ref={badgeRef}
                            type="button"
                            className={styles.badge}
                            onClick={openMenu}
                            aria-haspopup="menu"
                            aria-expanded={Boolean(menu)}
                            aria-label={`${folded.length} more levels`}
                        >
                            <MoreHorizontal size={12} strokeWidth={2.6} />
                            {folded.length}
                        </button>
                    </React.Fragment>
                )
            })}
            {menu &&
                createPortal(
                    <div
                        className={styles.menuBackdrop}
                        onClick={ev => {
                            ev.stopPropagation()
                            setMenu(null)
                        }}
                    >
                        <div
                            className={styles.menu}
                            role="menu"
                            style={{ top: menu.top, left: menu.left }}
                            onClick={ev => ev.stopPropagation()}
                        >
                            {folded.map(i => {
                                const answer = ancestors[i]
                                const { payload, label, isKind } = crumbParts(answer)
                                return (
                                    <button
                                        key={String(answer?.id ?? i)}
                                        type="button"
                                        role="menuitem"
                                        className={styles.menuItem}
                                        onClick={() => {
                                            setMenu(null)
                                            onOpen(i)
                                        }}
                                    >
                                        {answer?.user && (
                                            <UserIcon
                                                user={answer.user}
                                                contextStyles={styles.menuAvatar}
                                                size="small"
                                            />
                                        )}
                                        <span className={styles.menuText}>
                                            <span className={styles.menuName}>
                                                {answer?.user?.name || 'Someone'}
                                            </span>
                                            <span className={styles.menuWords}>
                                                <Thumb payload={payload} />
                                                <span className={cn(isKind && styles.kind)}>
                                                    {label}
                                                </span>
                                            </span>
                                        </span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>,
                    document.body,
                )}
        </span>
    )
}
